import React, { useState } from 'react';

import { Button, CardActions, CardContent, Icon, Theme, Typography } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { fade } from '@material-ui/core/styles/colorManipulator';
import Check from '@material-ui/icons/Check';
import Event from '@material-ui/icons/Event';
import QuestionAnswer from '@material-ui/icons/QuestionAnswer';
import classnames from 'classnames';
import { get, identity, isEmpty, map, pickBy } from 'lodash';
import { useDownloadPdfFile } from 'ui-model';

import { getEventTypeProps } from 'utils/utilsDesign';

const styles = (theme: Theme) =>
  createStyles({
    content: {
      paddingTop: 0,
      paddingLeft: theme.spacing.unit * 4,
      paddingRight: theme.spacing.unit * 4,
    },
    details: {
      borderLeft: '3px solid',
      marginLeft: 2,
      marginBottom: theme.spacing.unit * 2,
      paddingLeft: theme.spacing.unit * 1.5,
    },
    detail: {
      padding: '3px 0',
      display: 'flex',
      alignItems: 'center',
    },
    capitalized: {
      textTransform: 'capitalize',
    },
    detailText: {
      color: theme.palette.text.primary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    actions: {
      padding: 0,
      justifyContent: 'space-between',
    },
    button: {
      marginLeft: -1 * theme.spacing.unit,
      color: theme.palette.text.primary,
    },
    leftIcon: {
      marginRight: theme.spacing.unit / 1.5,
    },
    iconSmall: {
      fontSize: theme.spacing.unit * 2,
    },
  });

interface PatientJourneyCardContentProps extends WithStyles<typeof styles> {
  event: any;
  onClickMessages: any;
}

const PatientJourneyCardContent: React.FC<PatientJourneyCardContentProps> = ({ classes, event, onClickMessages }) => {
  const color = get(getEventTypeProps(event.type), 'color', '');
  const [isDownloadPdfTriggered, setDownloadPdfTrigger] = useState(false);
  const [receiptId, setReceiptId] = useState('');
  useDownloadPdfFile({
    url: `/billingTransactions/${receiptId}/pdf`,
    fileName: `receipt-${receiptId}.pdf`,
    isEnabled: isDownloadPdfTriggered,
    onSettled: () => setDownloadPdfTrigger(false),
  });
  const details = pickBy(
    {
      type: get(event, 'details.type.name'),
      provider: get(event, 'details.provider.name'),
      facility: get(event, 'details.facility.name'),
      feedback: get(event, 'details.response.value'),
      cpt: get(event, 'details.cpt'),
      icd: get(event, 'details.icd9') || get(event, 'details.icd10'),
      memberName: get(event, 'details.memberName'),
      memberNumber: get(event, 'details.memberNumber'),
      insuranceGroupName: get(event, 'details.insuranceGroupName'),
      planName: get(event, 'details.planName'),
      patientFormTitle: get(event, 'details.patientFormTemplate.title'),
      copayAmount: get(event, 'details.amount', 0),
      copayStatus: get(event, 'details.status'),
    },
    identity,
  );
  // Remove view messages button for diagnosis and procedures
  // https://app.clickup.com/t/b6rzb
  const messages = /diagnoses|procedures/.test(event.type) ? [] : get(event, 'details.messages.data');

  const handleReceiptDownload = () => {
    const id = get(event, 'details._id', '');

    if (id) {
      setReceiptId(id);
      setDownloadPdfTrigger(true);
    }
  };

  if (isEmpty(details) && isEmpty(messages)) {
    return <div />;
  }

  return (
    <CardContent className={classes.content}>
      {!isEmpty(details) && (
        <div className={classes.details} style={{ borderLeftColor: fade(color, 0.15) }}>
          {map(details, (value, key) => (
            <div key={key} className={classes.detail}>
              {/patientFormTitle/.test(key) && <Typography style={{ color, fontWeight: 600 }}>{`${value}`}</Typography>}
              {/cpt|icd/.test(key) && (
                <Typography style={{ color, fontWeight: 600 }}>{`${key.toUpperCase()} ${value}`}</Typography>
              )}
              {/copayAmount/.test(key) && (
                <>
                  <Icon
                    className={classnames(classes.leftIcon, classes.iconSmall)}
                    style={{ color: get(getEventTypeProps('billingTransactions'), 'color', '') }}
                  >
                    {get(getEventTypeProps('billingTransactions'), 'iconName', '')}
                  </Icon>
                  <Typography className={classes.detailText}>
                    {(value / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                </>
              )}
              {/copayStatus/.test(key) && (
                <>
                  <Check className={classnames(classes.leftIcon, classes.iconSmall)} style={{ color }} />
                  <Typography className={classnames(classes.detailText, classes.capitalized)}>
                    {value === 'not-paid' ? 'not paid' : value}
                  </Typography>
                </>
              )}
              {!/copayStatus|copayAmount|patientFormTitle|cpt|icd/.test(key) && (
                <>
                  <Icon className={classnames(classes.leftIcon, classes.iconSmall)} style={{ color }}>
                    {get(getEventTypeProps(key), 'iconName', '')}
                  </Icon>
                  <Typography className={classes.detailText}>{value}</Typography>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {event.type !== 'billingTransactions' && !isEmpty(messages) && (
        <CardActions className={classes.actions}>
          <Button className={classes.button} color="primary" size="small" onClick={onClickMessages}>
            <QuestionAnswer className={classnames(classes.leftIcon, classes.iconSmall)} />
            View messages
          </Button>
        </CardActions>
      )}
      {event.type === 'billingTransactions' && get(details, 'copayStatus', '') === 'paid' && (
        <CardActions className={classes.actions}>
          <Button className={classes.button} color="primary" size="small" onClick={handleReceiptDownload}>
            <Event className={classnames(classes.leftIcon, classes.iconSmall)} />
            View transaction
          </Button>
        </CardActions>
      )}
    </CardContent>
  );
};

export default withStyles(styles)(PatientJourneyCardContent);
